/**
 * Local-only CMS logout.
 *
 * Editors stay signed in at SWITCH edu-ID so a later CMS login can reuse the
 * existing SSO session. In particular, this component must never dispatch the
 * volto-authomatic `oidcLogout()` action or navigate to the provider.
 *
 * The confirmation toast matters here precisely because the SSO session
 * survives: without it, a failed logout looks exactly like a successful one.
 * It reuses core Volto's message ids, so no new translations are needed.
 */
import React, { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import qs from 'query-string';

import { logout, purgeMessages } from '@plone/volto/actions';
import { Toast } from '@plone/volto/components';

const messages = defineMessages({
  loggedOut: {
    id: 'Logged out',
    defaultMessage: 'Logged out',
  },
  loggedOutContent: {
    id: 'You have been logged out from the site.',
    defaultMessage: 'You have been logged out from the site.',
  },
});

const Logout = ({ location }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const token = useSelector((state) => state.userSession.token, shallowEqual);

  const returnUrl = useMemo(
    () =>
      qs.parse(location.search).return_url ||
      location.pathname.replace(/\/logout\/?$/, '') ||
      '/',
    [location.pathname, location.search],
  );

  useEffect(() => {
    dispatch(logout());
    dispatch(purgeMessages());
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      history.replace(returnUrl);
      if (!toast.isActive('loggedOut')) {
        toast.info(
          <Toast
            info
            title={intl.formatMessage(messages.loggedOut)}
            content={intl.formatMessage(messages.loggedOutContent)}
          />,
          { autoClose: false, toastId: 'loggedOut' },
        );
      }
    }
  }, [history, intl, returnUrl, token]);

  return null;
};

export default Logout;
